import {
    Card,
    CardContent,
    Divider,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { CardListRow } from '@starjack/shared-components';
import { Customer } from '@/models/customer';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import { usePutCustomerMutation } from '@/api/v2/customer';
import IconButton from '@mui/material/IconButton';
import { TrashIcon } from '@heroicons/react/24/solid';
import { LoadingContainer } from '@starjack/shared-components';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import { Permission } from '@/models/user';

type CustomerGroupsCardProps = {
    customer: Customer;
};

export const CustomerGroupsCard: FunctionComponent<CustomerGroupsCardProps> = ({
    customer,
}) => {
    const [nextGroup, setNextGroup] = useState('');
    const [groupDeleting, setGroupDeleting] = useState('');
    const [putCustomer, { isLoading: putCustomerLoading }] =
        usePutCustomerMutation();
    const { addNotification } = useNotification();
    const [isAuthorized] = useIsAuthorized([Permission.ADMIN]);

    const onAddNextGroup = () => {
        const nextCustomer = {
            ...customer,
            groups: [...customer.groups, nextGroup],
        };

        putCustomer(nextCustomer)
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Gruppe erfolgreich hinzugefügt',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Gruppe konnte nicht hinzugefügt werden. Bitte später noch einmal versuchen',
                });
            });
    };

    const onDeleteGroup = (group: string) => {
        const nextCustomer = {
            ...customer,
            groups: customer.groups.filter((g) => g !== group),
        };

        setGroupDeleting(group);
        putCustomer(nextCustomer)
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Gruppe erfolgreich gelöscht',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Löschen der Gruppe. Bitte später noch einmal versuchen',
                });
            })
            .finally(() => {
                setGroupDeleting('');
            });
    };

    return (
        <Card
            sx={{
                maxWidth: '640px',
            }}
        >
            <CardContent
                sx={{
                    bgcolor: 'divider',
                }}
            >
                <Typography variant='h6'>Gruppen</Typography>
            </CardContent>
            <Divider
                sx={{
                    width: '100%',
                }}
            />

            {customer.groups.map((group, index) => (
                <CardListRow
                    sx={{
                        position: 'relative',
                        alignItems: 'center',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                    }}
                    key={index}
                >
                    <Typography>{group}</Typography>

                    {isAuthorized && (
                        <IconButton
                            aria-label='Gruppe entfernen'
                            sx={{
                                ml: 'auto',
                            }}
                            onClick={() => onDeleteGroup(group)}
                        >
                            <SvgIcon fontSize='medium'>
                                <TrashIcon />
                            </SvgIcon>
                        </IconButton>
                    )}

                    {groupDeleting === group && <LoadingContainer cover />}
                </CardListRow>
            ))}

            {isAuthorized && (
                <CardContent>
                    <Stack spacing={2} direction='row' alignItems='center'>
                        <TextField
                            sx={{
                                width: 400,
                            }}
                            label='Gruppen hinzufügen'
                            onChange={(event) => {
                                const { value } = event.target;

                                setNextGroup(value);
                            }}
                        />
                        <LoadingButton
                            variant='contained'
                            onClick={onAddNextGroup}
                            disabled={!nextGroup}
                            loading={putCustomerLoading && !groupDeleting}
                        >
                            Gruppe hinzufügen
                        </LoadingButton>
                    </Stack>
                </CardContent>
            )}
        </Card>
    );
};
